import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getAuditSchedules, getAuditCalendar, AUDIT_TYPES } from '@/services/audit';
import { toast } from 'sonner';

const AuditSchedulePage = () => {
  const [schedules, setSchedules] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [schedulesData, calendarData] = await Promise.all([
        getAuditSchedules({ year: currentYear }),
        getAuditCalendar({ year: currentYear })
      ]);
      setSchedules(schedulesData.schedules || []);
      setCalendar(calendarData.events || []);
    } catch (error) {
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planned: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      review: 'bg-yellow-500',
      completed: 'bg-green-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Schedule</h1>
          <p className="text-gray-500 mt-1">Annual audit planning and calendar</p>
        </div>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Schedule
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Scheduled Audits</p>
                <p className="text-2xl font-bold">{calendar.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold">
                  {calendar.filter(e => e.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold">
                  {calendar.filter(e => e.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {currentYear} Audit Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-1 mb-4">
            {months.map((month, idx) => (
              <div key={month} className="text-center text-xs font-medium text-gray-500 py-2">
                {month}
              </div>
            ))}
          </div>
          
          {calendar.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audits scheduled for {currentYear}</p>
              <Button variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Schedule First Audit
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {calendar.map(event => {
                const startDate = new Date(event.start);
                const endDate = new Date(event.end);
                const startMonth = startDate.getMonth();
                const endMonth = endDate.getMonth();
                const span = endMonth - startMonth + 1;
                
                return (
                  <div key={event.id} className="grid grid-cols-12 gap-1 items-center">
                    {[...Array(12)].map((_, idx) => (
                      <div key={idx} className="h-8">
                        {idx === startMonth && (
                          <div 
                            className={`h-full ${getStatusColor(event.status)} text-white text-xs rounded px-2 flex items-center truncate`}
                            style={{ gridColumn: `span ${span}` }}
                          >
                            {event.title}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scheduled Audits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {calendar.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No audits scheduled</p>
            ) : (
              calendar.map(event => {
                const typeInfo = AUDIT_TYPES.find(t => t.value === event.audit_type) || {};
                return (
                  <div key={event.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                    <span className="text-2xl">{typeInfo.icon || '📋'}</span>
                    <div className="flex-1">
                      <p className="font-medium">{event.title}</p>
                      <p className="text-sm text-gray-500">
                        {typeInfo.label} • {event.lead_auditor}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {new Date(event.start).toLocaleDateString()} - {new Date(event.end).toLocaleDateString()}
                      </p>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        event.status === 'completed' ? 'bg-green-100 text-green-700' :
                        event.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {event.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditSchedulePage;